import type { FestivalBasis } from "@/lib/festival-answer";
import { FESTIVAL_FACTS } from "@/lib/festival-facts.generated";

export const TYPE_LABELS: Record<string, string> = {
  festival: "Festival",
  vrat: "Vrat",
  eclipse: "Eclipse",
  tithi_based: "Tithi-based",
};

// Hindi twin of TYPE_LABELS — the type badge on /hi/calendar/festival/<id>/.
export const TYPE_LABELS_HI: Record<string, string> = {
  festival: "पर्व",
  vrat: "व्रत",
  eclipse: "ग्रहण",
  tithi_based: "तिथि-आधारित",
};

// Presentation-only badge roles. Festival facts remain in festivals.yaml and the
// generated fact modules; these values only select existing semantic utilities.
export const TYPE_BADGE_CLASSES: Record<string, string> = {
  festival: "border-warning/30 bg-warning/10 text-warning",
  vrat: "border-success/30 bg-success/10 text-success",
  eclipse: "border-intent-brand/30 bg-surface-brand-subtle text-intent-brand",
  tithi_based: "border-info/30 bg-info/10 text-info",
};

/**
 * The Hindu month a festival page names in its lede and header.
 *
 * A festival with a tithi rule has ONE month: the rule's, which the "Panchang
 * Basis (Tithi Rule)" block prints from the same generated facts. Reading the
 * backend's separate `month_hi` here let the two disagree on the same page
 * (2026-09-28: Tulsi Vivah said Kartika above and Margashirsha below), and a
 * backend deploy that lags the web one would reopen that gap. A festival with no
 * tithi rule (an eclipse, Onam) keeps the backend's `month_hi`.
 */
export function festivalMonthHi(festival: { id: string; month_hi?: string | null }): string {
  const facts = FESTIVAL_FACTS[festival.id];
  if (facts?.rule_type === "tithi" && facts.hindu_month_hi) return facts.hindu_month_hi;
  return festival.month_hi?.trim() ?? "";
}

/**
 * Whether a festival page marks the day a fast (badge, lede and FAQ).
 *
 * Read from the generated facts for the same reason as the month: they ship with
 * the web build, so a web deploy that lands before the backend one cannot show a
 * "Fasting Day" badge the source no longer marks (2026-09-28: two eclipses not
 * visible from India carried `fasting: true`). An id the facts do not carry
 * keeps the backend's flag.
 */
export function festivalFasting(festival: { id?: string; fasting?: boolean | null }): boolean {
  const facts = festival.id ? FESTIVAL_FACTS[festival.id] : undefined;
  if (typeof facts?.fasting === "boolean") return facts.fasting;
  return festival.fasting === true;
}

/** What fixes a festival's day: its tithi rule, a Sankranti, or an eclipse. */
export function festivalBasis(festival: { id: string; type?: string | null }): FestivalBasis {
  if (festival.type === "eclipse") return "eclipse";
  const ruleType = FESTIVAL_FACTS[festival.id]?.rule_type;
  if (ruleType === "tithi") return "tithi";
  if (ruleType === "sankranti" || festival.id.startsWith("makar_sankranti")) return "sankranti";
  return "other";
}

/**
 * The date a festival page is about. A year-suffixed id (`pradosh_vrat_oct_shukla_2026`)
 * pins that year; otherwise the next date on or after `today`, else the latest one.
 * Titles used `new Date().getFullYear()`, so all 24 Pradosh occurrences shared one
 * title and `solar_eclipse_aug_2027` was titled 2026.
 */
export function festivalOccurrenceDate(
  id: string,
  festival: { date_2026?: string; date_2027?: string } | null,
  today: Date = new Date()
): string | null {
  if (!festival) return null;
  const pinned = id.match(/_(2026|2027)$/)?.[1];
  if (pinned === "2026" && festival.date_2026) return festival.date_2026;
  if (pinned === "2027" && festival.date_2027) return festival.date_2027;
  const dates = [festival.date_2026, festival.date_2027].filter((d): d is string => Boolean(d));
  if (dates.length === 0) return null;
  const todayIso = today.toISOString().slice(0, 10);
  return dates.find((d) => d >= todayIso) ?? dates[dates.length - 1];
}

/** "24 October 2026" / "24 अक्तूबर 2026" — the occurrence date for a title. */
export function formatTitleDate(dateStr: string, locale: "en" | "hi"): string {
  return new Date(dateStr + "T00:00:00").toLocaleDateString(locale === "hi" ? "hi-IN" : "en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** "Wednesday, 2 September 2026" / "बुधवार, 2 सितंबर 2026". */
export function formatDate(dateStr: string, locale: "en" | "hi" = "en"): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr + "T00:00:00");
  if (locale === "hi") {
    return d.toLocaleDateString("hi-IN", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }
  return d.toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

interface FestivalFaqSource {
  id?: string;
  name_en: string;
  name_hi: string;
  date_2026: string;
  date_2027: string;
  fasting: boolean;
  type?: string | null;
}

interface FestivalFaq {
  question: string;
  answer: string;
}

function englishDateFaq(name: string, date2026: string, date2027: string): FestivalFaq | null {
  if (!date2026 && !date2027) return null;
  if (date2026 && date2027) {
    return {
      question: `When is ${name} celebrated?`,
      answer: `${name} is celebrated on ${date2026} and on ${date2027}. The date changes each year because it follows the Hindu lunar calendar (Panchang) — every date here is computed from real planetary positions using the Swiss Ephemeris engine, not copied from a fixed list.`,
    };
  }
  return {
    question: `When is ${name} celebrated?`,
    answer: `${name} is celebrated on ${date2026 || date2027}. The date follows the Hindu lunar calendar (Panchang) and is computed from real planetary positions using the Swiss Ephemeris engine.`,
  };
}

function hindiDateFaq(name: string, date2026: string, date2027: string): FestivalFaq | null {
  if (!date2026 && !date2027) return null;
  if (date2026 && date2027) {
    return {
      question: `${name} कब है?`,
      answer: `${name} 2026 में ${date2026} को और 2027 में ${date2027} को है। यह तिथि हर वर्ष हिंदू पंचांग के अनुसार बदलती है, इसलिए अंग्रेज़ी तारीख़ एक वर्ष से दूसरे वर्ष में अलग होती है।`,
    };
  }
  return {
    question: `${name} कब है?`,
    answer: `${name} ${date2026 || date2027} को है। यह तिथि हिंदू पंचांग के अनुसार तय होती है।`,
  };
}

function englishFastingFaq(name: string, fasting: boolean): FestivalFaq {
  return {
    question: `Is ${name} a fasting (vrat) day?`,
    answer: fasting
      ? `Yes — ${name} is observed as a vrat (fasting day). Devotees keep a fast and complete the puja before breaking it, following the puja vidhi for the day.`
      : `${name} is not primarily a fasting day, though many families still keep a light fast or a sattvic diet as part of the observance.`,
  };
}

function hindiFastingFaq(name: string, fasting: boolean): FestivalFaq {
  return {
    question: `क्या ${name} व्रत का दिन है?`,
    answer: fasting
      ? `हाँ — ${name} व्रत (उपवास) के रूप में रखा जाता है। भक्त उपवास रखते हैं और पूजा विधि पूरी करने के बाद ही व्रत खोलते हैं।`
      : `${name} मुख्य रूप से व्रत का दिन नहीं है, फिर भी कई परिवार इस दिन सात्विक आहार या हल्का उपवास रखते हैं।`,
  };
}

/**
 * An eclipse is not "celebrated", does not follow a yearly tithi, and its fast
 * applies only where it is visible. The generic festival answers said all three
 * wrong things, and a not-visible eclipse read "many families still keep a light
 * fast". Every eclipse id is a single event with one date. `fasting` is true only
 * for an eclipse visible from India (tests/test_festival_eclipse_dates.py).
 */
function eclipseFaqs(
  name: string,
  date: string,
  fasting: boolean,
  locale: "en" | "hi"
): FestivalFaq[] {
  if (locale === "hi") {
    const fastingFaq: FestivalFaq = {
      question: `क्या ${name} पर व्रत रखा जाता है?`,
      answer: fasting
        ? "जहां ग्रहण दिखाई दे, वहां परंपरा में सूतक माना जाता है और ग्रहण समाप्त होने तक भोजन नहीं किया जाता; उसके बाद स्नान और दान किया जाता है।"
        : "भारत में इस ग्रहण के लिए कोई व्रत या सूतक निर्धारित नहीं है। ग्रहण का व्रत और सूतक केवल वहीं माने जाते हैं जहां ग्रहण दिखाई दे।",
    };
    if (!date) return [fastingFaq];
    return [
      {
        question: `${name} कब है?`,
        answer: `${name} ${date} को है। ग्रहण की तारीख और समय सूर्य और चंद्रमा की स्थिति से गणना करके निकाले जाते हैं; यह किसी वार्षिक तिथि से तय नहीं होता।`,
      },
      fastingFaq,
    ];
  }
  const fastingFaq: FestivalFaq = {
    question: `Is ${name} a fasting day?`,
    answer: fasting
      ? "Where the eclipse is visible, tradition keeps sutak and does not eat until the eclipse ends; a bath and daan follow."
      : "No eclipse fast or sutak is marked for India for this eclipse. The eclipse fast and sutak are kept only where an eclipse is visible.",
  };
  if (!date) return [fastingFaq];
  return [
    {
      question: `When is ${name}?`,
      answer: `${name} falls on ${date}. Eclipse dates and times are computed from the positions of the Sun and Moon with the Swiss Ephemeris; they are not set by a yearly tithi.`,
    },
    fastingFaq,
  ];
}

/**
 * Festival FAQ — the single source for BOTH the visible FAQ render (FestivalBody)
 * and the FAQPage JSON-LD (`pageSeoJsonLd({ faqs })` on the en + hi festival
 * pages). One function → identical question/answer text on both surfaces, so the
 * schema can never drift from the visible copy (rule 49 faq_parity; rules 23/24
 * "drifting copies").
 *
 * Every fact is ENGINE-CANONICAL: the dates come from `festival.date_20XX` via
 * `formatDate` (a structured field, never an LLM/literal date — rule 24) and
 * fasting from `festivalFasting`. Significance stays in its dedicated visible section;
 * repeating that same backend prose as an FAQ answer creates duplicate body copy.
 * The answers are "answer-first" because festival "when is X / is X a vrat"
 * queries are exactly the zero-click queries this cluster must remain the cited
 * SOURCE for.
 */
export function festivalFaqs(
  festival: FestivalFaqSource,
  locale: "en" | "hi" = "en"
): FestivalFaq[] {
  const isHindi = locale === "hi";
  const name = isHindi ? festival.name_hi : festival.name_en;
  const date2026 = festival.date_2026 ? formatDate(festival.date_2026, locale) : "";
  const date2027 = festival.date_2027 ? formatDate(festival.date_2027, locale) : "";
  const fasting = festivalFasting(festival);
  if (festival.type === "eclipse") {
    return eclipseFaqs(name, date2026 || date2027, fasting, locale);
  }
  const dateFaq = isHindi
    ? hindiDateFaq(name, date2026, date2027)
    : englishDateFaq(name, date2026, date2027);
  const fastingFaq = isHindi ? hindiFastingFaq(name, fasting) : englishFastingFaq(name, fasting);

  return dateFaq ? [dateFaq, fastingFaq] : [fastingFaq];
}
